import { useState, useEffect } from "react";
import RichTextEditor, { BaseKit } from "reactjs-tiptap-editor";
import { Blockquote } from "reactjs-tiptap-editor/blockquote";
import { Bold } from "reactjs-tiptap-editor/bold";
import { Italic } from "reactjs-tiptap-editor/italic";
import { Strike } from "reactjs-tiptap-editor/strike";
import { Heading } from "reactjs-tiptap-editor/heading";
import { Link } from "reactjs-tiptap-editor/link";
import { Image } from "reactjs-tiptap-editor/image";
import { Video } from "reactjs-tiptap-editor/video";
import { TextAlign } from "reactjs-tiptap-editor/textalign";
import { TextUnderline } from "reactjs-tiptap-editor/textunderline";
import { TrailingNode } from "reactjs-tiptap-editor/trailingnode";
import { TextBubble } from "reactjs-tiptap-editor/textbubble";
import { OrderedList } from "reactjs-tiptap-editor/orderedlist";
import { History } from "reactjs-tiptap-editor/history";
import { Highlight } from "reactjs-tiptap-editor/highlight";
import { Clear } from "reactjs-tiptap-editor/clear";
import { Color } from "reactjs-tiptap-editor/color";
import { FontFamily } from "reactjs-tiptap-editor/fontfamily";
import { FontSize } from "reactjs-tiptap-editor/fontsize";
import { BulletList } from "reactjs-tiptap-editor/bulletlist";
import "reactjs-tiptap-editor/style.css";
import { Media_Format_and_size_messages } from "../utils/constants";

const showPopupError = (message) => {
  try {
    const findUploadButton = () => {
      // find all buttons whose text contains "upload" (case-insensitive)
      const uploadButtons = Array.from(
        document.querySelectorAll("button")
      ).filter((b) => /upload/i.test((b.textContent || "").trim()));
      if (uploadButtons.length === 0) return null;

      return uploadButtons[1]; // otherwise use the first upload button found
    };

    const uploadBtn = findUploadButton();
    console.debug(
      "TipTapEditor: showPopupError target button:",
      uploadBtn,
      "message:",
      message
    );

    // remove previous detached element
    let el = document.getElementById("tiptap-upload-error");

    if (!el) {
      el = document.createElement("div");
      el.id = "tiptap-upload-error";
      el.className = "tiptap-upload-error";
    }

    el.textContent = message;

    if (uploadBtn) {
      const parent = uploadBtn.parentElement;
      if (parent && getComputedStyle(parent).position === "static") {
        parent.style.position = "relative";
      }
      // insert after the upload button so it appears below it
      uploadBtn.insertAdjacentElement("afterend", el);
      // div.insertAdjacentElement("afterend", el)
    }
  } catch (e) {
    try {
      window.alert(message);
    } catch (e) {}
  }
};

const TipTapEditor = ({ content, onChange }) => {
  const [editorContent, setEditorContent] = useState(content);

  useEffect(() => {
    const interval = setInterval(() => {
      document.querySelectorAll("button").forEach((btn) => {
        if (btn.textContent.trim() === "Upload & Crop") {
          btn.style.display = "none";
        }
      });

      const uploadTrigger = document.querySelector(
        '[id^="radix-"][id*="trigger-upload"]'
      );

      if (uploadTrigger) {
        uploadTrigger.style.display = "none";
        const parent = uploadTrigger.parentElement;
        parent.style.display = "none";
      }

      document.getElementById("radix-:r11:") &&
        (document.getElementById("radix-:r11:").textContent = "Upload Video");
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const extensions = [
    BaseKit.configure({
      placeholder: false,
      characterCount: false,
    }),
    History,
    Clear,
    FontFamily,
    Heading.configure({ spacer: true }),
    FontSize,
    Bold.configure({ spacer: true }),
    Italic,
    Strike,
    TextUnderline,
    Color.configure({ spacer: true }),
    Highlight,
    BulletList,
    OrderedList,
    TextAlign.configure({ types: ["heading", "paragraph"], spacer: true }),
    Link,
    Blockquote,
    TrailingNode,
    TextBubble,

      Image.configure({
        spacer: true,
        resourceImage: "upload",
        acceptMimes: ["image/jpeg", "image/png", "image/jpg"],
        upload: (file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              resolve(reader.result); // Returns base64 data URL
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
        },
        maxSize: 50 * 1024 * 1024, //50Mb
        onError: (err) => {
          const message =
            err?.message ||
            (typeof err === "string" ? err : String(err)) ||
            "Image upload error";
          try {
            if (err.type === "size") {
              showPopupError(Media_Format_and_size_messages.image_size_message);
            }
            if (err.type === "type") {
              showPopupError(Media_Format_and_size_messages.image_type_message);
            }
            if (err.type === "upload") {
              showPopupError(`Upload failed: ${err.message}`);
            }
          } catch (e) {
            try {
              window.alert(message);
            } catch (e) {}
          }
          console.error("TipTapEditor: image error:", err);
        },
      }),
      Video.configure({
        resourceVideo: "upload",
        upload: (file) => {
          return new Promise((resolve, reject) => {
            const MAX_SIZE = 500 * 1024 * 1024; // 100 MB

            if (!(file instanceof File)) {
              const msg = "Only file uploads are allowed.";
              showPopupError(msg);
              return reject(new Error(msg));
            }

            // allow only mp4 and webm by MIME or extension
            const allowedMimes = ["video/mp4", "video/webm"];
            const allowedExt = /\.(mp4|webm)$/i;
            const isMimeOk = !!file.type && allowedMimes.includes(file.type);
            const isExtOk = allowedExt.test(file.name || "");
            if (!isMimeOk && !isExtOk) {
              const msg = Media_Format_and_size_messages.video_type_message;
              showPopupError(msg);
              return reject(new Error(msg));
            }

            if (file.size > MAX_SIZE) {
              const msg = Media_Format_and_size_messages.video_size_message;
              showPopupError(msg);
              return reject(new Error(msg));
            }

            // If file is valid → create preview URL
            resolve(URL.createObjectURL(file));
          });
        },
      })
  ];

  return (
    <RichTextEditor
      content={editorContent} // controlled editor content
      onChangeContent={(newContent) => {
        setEditorContent(newContent); // keep local state
        onChange?.(newContent); // forward to parent
      }}
      extensions={extensions}
      output="html"
      dark={false}
    />
  );
};

export default TipTapEditor;
